import { apiGet, apiPatch } from '../utils/apiClient';

export interface TradingStatistics {
  totalGames: number;
  winCount: number;
  lossCount: number;
  passCount: number; // 패스 횟수 (profit === 0, 거래 없이 종료)
  winRate: number; // 승률 (%) - 실제 거래한 게임 중 승리 비율
  totalProfit: number;
  totalLoss: number;
  profitLossRatio: number | null; // Infinity인 경우 null
  averageProfit: number;
  averageProfitRate: number;
  averageLoss: number;
  averageLossRate: number;
  bestProfit: number;
  bestProfitRate: number;
  worstLoss: number;
  worstLossRate: number;
  profitByMonth: Array<{ month: string; profit: number }>;
}

export interface Achievement {
  achievementId: number;
  code: string;
  name: string;
  description: string;
  tier: string;
  icon: string;
  isUnlocked: boolean;
  unlockedAt: string | null;
}

export interface Title {
  titleId: number;
  code: string;
  name: string;
  description: string;
  rarity: string;
  icon: string;
  requirements: {
    gameCount?: number;
    maxBalance?: number;
    firstProfit?: boolean;
    winStreak?: number;
    bestRank?: number;
    minProfitRate?: number;
    singleGameProfitRate?: number;
    winRate?: number;
    recoveryFromLoss?: boolean;
  };
  isUnlocked: boolean;
  unlockedAt: string | null;
}

export interface MyPageData {
  user: {
    email: string;
    nickname: string;
    balance: number;
    gameCount: number;
    maxBalance: number;
    bestRank: number;
    currentRank: number;
    coinCount: number; // AI 분석 토큰
    isPublicProfile: boolean;
    createdAt: string;
    lastPublicToggleDate: string | null;
    canToggleAgainAt: string | null;
    lastNicknameChangeDate: string | null;
    canChangeNicknameAt: string | null;
  };
  statistics: TradingStatistics;
  tradingHistoryCount: number; // 매매내역 개수
  achievements: Achievement[];
  titles: Title[];
  selectedTitle: {
    titleId: number;
    code: string;
    name: string;
    icon: string;
    rarity: string;
  } | null;
}

/**
 * 마이페이지 데이터 캐시 (1분)
 */
let myPageCache: {
  data: MyPageData | null;
  timestamp: number;
} | null = null;

const MY_PAGE_CACHE_DURATION = 1 * 60 * 1000; // 1분

/**
 * 캐시된 마이페이지 데이터 조회
 */
export const getMyPageData = async (
  apiBaseUrl: string,
  forceRefresh = false
): Promise<MyPageData> => {
  // 캐시 확인
  if (
    !forceRefresh &&
    myPageCache &&
    Date.now() - myPageCache.timestamp < MY_PAGE_CACHE_DURATION &&
    myPageCache.data !== null
  ) {
    console.log('✅ 마이페이지 캐시 HIT');
    return myPageCache.data;
  }

  try {
    const response = await apiGet<MyPageData>(
      `${apiBaseUrl}/api/user/mypage`
    );
    
    // 디버깅: 응답 구조 확인
    if (!response) {
      console.error('마이페이지 응답이 비어있습니다:', response);
      throw new Error('마이페이지 데이터를 받을 수 없습니다.');
    }
    
    if (!response.user) {
      console.error('마이페이지 응답에 user가 없습니다:', response);
      throw new Error('사용자 정보를 받을 수 없습니다.');
    }
    
    // 디버깅: tradingHistoryCount 확인
    console.log('📊 마이페이지 데이터:', {
      tradingHistoryCount: response.tradingHistoryCount,
      hasTradingHistoryCount: 'tradingHistoryCount' in response,
      responseKeys: Object.keys(response),
    });
    
    // canToggleAgainAt, canChangeNicknameAt이 없을 경우 null로 설정
    // user가 없을 경우를 대비한 안전한 처리
    const myPageData: MyPageData = {
      ...response,
      tradingHistoryCount: response.tradingHistoryCount ?? 0, // 기본값 설정
      user: {
        ...response.user,
        canToggleAgainAt: response.user.canToggleAgainAt ?? null,
        lastPublicToggleDate: response.user.lastPublicToggleDate ?? null,
        canChangeNicknameAt: response.user.canChangeNicknameAt ?? null,
        lastNicknameChangeDate: response.user.lastNicknameChangeDate ?? null,
      },
    };

    // 성공한 데이터만 캐시 저장
    myPageCache = {
      data: myPageData,
      timestamp: Date.now(),
    };
    console.log('💾 마이페이지 캐시 저장');

    return myPageData;
  } catch (error) {
    console.error('getMyPageData 오류:', error);
    throw error;
  }
};

/**
 * 마이페이지 캐시 무효화 (데이터 변경 시 호출)
 */
export const invalidateMyPageCache = (): void => {
  myPageCache = null;
  console.log('🗑️ 마이페이지 캐시 무효화');
};

export const selectTitle = async (
  apiBaseUrl: string,
  titleId: number
): Promise<void> => {
  await apiPatch(`${apiBaseUrl}/api/user/mypage/title`, { titleId });
  // 칭호 선택 시 마이페이지 캐시 무효화
  invalidateMyPageCache();
};

export const togglePublicProfile = async (
  apiBaseUrl: string,
  isPublic: boolean
): Promise<{
  isPublicProfile: boolean;
  canToggleAgainAt: string | null;
}> => {
  // apiRequest가 이미 data를 추출하므로, 직접 { isPublicProfile, canToggleAgainAt } 형태로 받음
  const response = await apiPatch<{
    isPublicProfile: boolean;
    canToggleAgainAt: string | null;
  }>(`${apiBaseUrl}/api/user/mypage/public-profile`, { isPublic });
  
  // 안전하게 처리
  if (!response) {
    throw new Error('프로필 공개 상태 변경 응답을 받을 수 없습니다.');
  }
  
  // 프로필 공개 상태 변경 시 마이페이지 캐시 무효화
  invalidateMyPageCache();
  
  return {
    isPublicProfile: response.isPublicProfile ?? false,
    canToggleAgainAt: response.canToggleAgainAt ?? null,
  };
};
